import { ScheduleServiceDialog } from "@/components/site/ScheduleServiceDialog";
import { Link } from "@/i18n/Link";
import { ArrowRight, BadgePercent } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    trail: [{ name: "Home", path: "/" }, { name: "Rebates", path: "/rebates/" }],
    seoTitle: "Dexter Trailer Axle Rebates & Promotions - Elkhart, IN",
    seoDesc: "Current Dexter Group rebates on trailer axles, brake assemblies, and running gear at Axle Inc. in Elkhart, IN. We process the paperwork - you keep the savings.",
    header: "Rebates & Promotions",
    title: "Save on what you were going to buy anyway.",
    subtitle: "Dexter Group runs ongoing customer rebates on axles, brake assemblies, and parts. As an authorized distributor, we process them directly - you don't fill out a form and wait three months.",
    card1: {
      tag: "Current",
      title: "Axle Rebate On New Purchases",
      desc: "Customer rebate program returns money on qualifying axle and brake assembly purchases. Eligibility, amounts, and end dates rotate quarterly. Call the shop for the current schedule that applies to your purchase, some restrictions may apply contact Axle Inc with any questions.",
      btn: "View offer"
    },
    card2: {
      tag: "Ongoing - Axle Inc.",
      title: "Fleet & Wholesale Pricing",
      desc: "Local dealers, manufacturers, and fleets qualify for our wholesale program - better pricing, dedicated stock, and direct access to our service team. Set up an account in 10 minutes by phone.",
      btn: "Set up an account"
    },
    card3: {
      tag: "Service",
      title: "First-Time Service Customer",
      desc: "New to Axle Inc.? Mention this page when you schedule a repair and we'll waive the diagnostic fee on your first visit. We just want a chance to earn the relationship.",
      btn: "Schedule service"
    },
    footer: "Promotions, rebate amounts, and terms change. The most current information is always available by calling the shop. Some promotions are not combinable; ask when you order."
  },
  es: {
    trail: [{ name: "Inicio", path: "/" }, { name: "Reembolsos", path: "/rebates/" }],
    seoTitle: "Reembolsos y promociones de ejes de remolque Dexter - Elkhart, IN",
    seoDesc: "Reembolsos actuales del Dexter Group en ejes de remolque, conjuntos de frenos y trenes de rodaje en Axle Inc. en Elkhart, IN. Nosotros procesamos el papeleo, usted se queda con los ahorros.",
    header: "Reembolsos y Promociones",
    title: "Ahorre en lo que iba a comprar de todos modos.",
    subtitle: "Dexter Group ofrece reembolsos continuos a los clientes en ejes, conjuntos de frenos y repuestos. Como distribuidor autorizado, los procesamos directamente; usted no tiene que llenar un formulario ni esperar tres meses.",
    card1: {
      tag: "Actual",
      title: "Reembolso en nuevas compras de ejes",
      desc: "El programa de reembolso al cliente devuelve dinero en compras que califiquen de ejes y conjuntos de frenos. La elegibilidad, los montos y las fechas de finalización rotan trimestralmente. Llame al taller para conocer el calendario actual que se aplica a su compra, pueden aplicarse algunas restricciones, contacte a Axle Inc si tiene preguntas.",
      btn: "Ver oferta"
    },
    card2: {
      tag: "En curso - Axle Inc.",
      title: "Precios para flotas y mayoristas",
      desc: "Los concesionarios locales, fabricantes y flotas califican para nuestro programa mayorista: mejores precios, inventario dedicado y acceso directo a nuestro equipo de servicio. Abra una cuenta en 10 minutos por teléfono.",
      btn: "Abrir una cuenta"
    },
    card3: {
      tag: "Servicio",
      title: "Cliente de servicio por primera vez",
      desc: "¿Es nuevo en Axle Inc.? Mencione esta página cuando programe una reparación y eliminaremos el cargo por diagnóstico en su primera visita. Solo queremos tener la oportunidad de ganarnos su confianza.",
      btn: "Programar servicio"
    },
    footer: "Las promociones, los montos de reembolso y los términos pueden cambiar. La información más actualizada siempre está disponible llamando al taller. Algunas promociones no son acumulables; pregunte al realizar su pedido."
  }
};

export default function Rebates() {
  const t = useCopy(COPY);
  const trail = t.trail;

  return (
    <PageLayout>
      <Seo
        title={t.seoTitle}
        description={t.seoDesc}
        path="/rebates/"
        jsonLd={[organizationJsonLd(), breadcrumbJsonLd(trail)]}
      />
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4 inline-flex items-center gap-2">
            <BadgePercent className="h-4 w-4" /> {t.header}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
            {t.title}
          </h1>
          <p className="mt-5 text-lg text-secondary-foreground/80 max-w-2xl leading-relaxed">
            {t.subtitle}
          </p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20 max-w-4xl">
        <div className="grid gap-5">
          <div className="border border-border rounded-md bg-card p-6 md:p-8">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-primary-strong font-semibold mb-2">{t.card1.tag}</p>
                <h2 className="text-xl md:text-2xl font-semibold">{t.card1.title}</h2>
                <p className="mt-2 text-muted-foreground leading-relaxed max-w-xl">
                  {t.card1.desc}
                </p>
              </div>
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/rebates/axle-rebate/">
                  {t.card1.btn} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="border border-border rounded-md bg-card p-6 md:p-8">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-primary-strong font-semibold mb-2">{t.card2.tag}</p>
                <h2 className="text-xl md:text-2xl font-semibold">{t.card2.title}</h2>
                <p className="mt-2 text-muted-foreground leading-relaxed max-w-xl">
                  {t.card2.desc}
                </p>
              </div>
              <Button asChild variant="outline">
                <Link to="/contact/">{t.card2.btn} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
            </div>
          </div>

          <div className="border border-border rounded-md bg-card p-6 md:p-8">
            <div className="flex items-start justify-between flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-primary-strong font-semibold mb-2">{t.card3.tag}</p>
                <h2 className="text-xl md:text-2xl font-semibold">{t.card3.title}</h2>
                <p className="mt-2 text-muted-foreground leading-relaxed max-w-xl">
                  {t.card3.desc}
                </p>
              </div>
              <ScheduleServiceDialog>
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90">
                  {t.card3.btn} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              </ScheduleServiceDialog>
            </div>
          </div>
        </div>

        <p className="mt-10 text-sm text-muted-foreground leading-relaxed">
          {t.footer}
        </p>
      </section>
    </PageLayout>
  );
}
